import { useFetchImageList, usePutImageUse } from '@api';
import { optionsConstant, PathnameEnum, QueryParamKeyEnum, ToastMessageEnum } from '@constant';
import {
  useActionGenreActions,
  useAdultsOnlyActions,
  useBodyPartActions,
  useEtcActions,
  useFacialExpressionActions,
  useStandardActions
} from '@remote-hooks';
import { useSelector } from '@stores';
import { useQueryClient } from '@tanstack/react-query';
import { IImageListItem } from '@types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useToast } from './useToast';

//Output페이지 및 GET /images 로직에 관여
export const useOutput = (scrollToTop?: () => void) => {
  const minColumnNum = 6;
  const maxColumnNum = 11;
  const [columnNum, setColumnNum] = useState<number>(minColumnNum);

  const storedComic = useSelector((state) => state.comic);
  const storedMetaTag = useSelector((state) => state.metaTag);
  const storedStandard = useSelector((state) => state.standard);
  const storedAdultsOnly = useSelector((state) => state.adultsOnly);
  const storedActionGenre = useSelector((state) => state.actionGenre);
  const storedFacialExpressionChildOptionValue = useSelector((state) => state.facialExpression);
  const storedBodyPartChildOptionValue = useSelector((state) => state.bodyPart);
  const storedEtcChildOptionValue = useSelector((state) => state.etc);

  //comic: 장르 및 작품명
  const selectedGenreList = storedComic.genreList;
  const selectedComicNameList = storedComic.comicNameList;

  //metaTag: 태그 검색 인풋에 입력된 메타 태그 & 글콘티 AI 추천 태그
  const selectedMetaTags = storedMetaTag.selectedMetaTags;
  const aiRecommendedMetaTags = storedMetaTag.aiRecommendedMetaTags;

  const { onSetStandard } = useStandardActions();
  const { onSetAdultsOnly } = useAdultsOnlyActions();
  const { onSetActionGenre } = useActionGenreActions();
  const { onSetBodyPart } = useBodyPartActions();
  const { onSetFacialExpression } = useFacialExpressionActions();
  const { onSetEtc } = useEtcActions();

  const { showToast } = useToast();

  const navigate = useNavigate();
  const outputBottomRef = useRef<HTMLDivElement | null>(null);
  const outputBottomRefCurrent = outputBottomRef.current;
  const location = useLocation();
  const locationPathname = location.pathname;
  const queryClient = useQueryClient();

  const createQueryStringForImageList = () => {
    const temp = [];
    const standardSearchOutputTake = 18;
    const otherOutputTake = 18;

    //스탠다드 검색
    if (locationPathname === PathnameEnum.STANDARD_OUTPUT_PAGE) {
      temp.push(`&take=${standardSearchOutputTake}`);

      //장르
      if (selectedGenreList && selectedGenreList.length > 0) {
        const selectedGenreStr = selectedGenreList.map((item) => `genre=${encodeURIComponent(item)}`).join('&');
        temp.push(`&${selectedGenreStr}`);
      }
      //작품명
      if (selectedComicNameList && selectedComicNameList.length > 0) {
        const selectedComicNameStr = selectedComicNameList.map((item) => `comic=${encodeURIComponent(item)}`).join('&');
        temp.push(`&${selectedComicNameStr}`);
      }

      // 내가 사용한 이미지 포함 여부
      temp.push(`&isIncludeUsed=${storedStandard.isIncludeUsed}`);

      // 남이 사용한 이미지 포함 여부
      temp.push(`&isIncludeOtherUsersUsed=${storedStandard.isIncludeOtherUsersUsed}`);

      // 정렬 기준
      temp.push(`&sortType=${storedStandard.sortType}`);

      //parentOption이 GENERAL인 옵션에 대한 query param 생성
      //parentOption이 GENERAL이 아닌 옵션에 대한 query param보다 앞 쪽에 위치하여야 한다 (14차 스프린트 사항)
      //인원수, 샷 구분, 가로앵글, 세로앵글
      //general이 아닌 standard store에서 가져온 값
      if (
        storedStandard.characterNumber ||
        storedStandard.shot ||
        storedStandard.horizontalAngle ||
        storedStandard.verticalAngle
      ) {
        temp.push(`&parentOptions=${optionsConstant.parentOptions['GENERAL'].id}`);

        //인원수
        if (storedStandard.characterNumber) {
          if (storedStandard.characterNumber.isWholeGenderNumberChecked) {
            temp.push(
              `&prop.${optionsConstant.childOptions['wholeGenderNumber'].id}=${encodeURIComponent(storedStandard.characterNumber.wholeGenderNumber ?? 0)}`
            );
          } else {
            temp.push(
              `&prop.${
                optionsConstant.childOptions['maleNumber'].id
              }=${encodeURIComponent(storedStandard.characterNumber.maleNumber ?? 0)}&prop.${optionsConstant.childOptions['femaleNumber'].id}=${encodeURIComponent(storedStandard.characterNumber.femaleNumber ?? 0)}`
            );
          }
        }

        //프레임
        if (storedStandard.shot) {
          const selectedFrameChildOptionValuesArr = Object.values(storedStandard.shot)
            .filter((item) => item.isSelected)
            .map((item) => item.value);

          //선택한 프레임 값이 1개 이상일 경우에만 진행
          if (selectedFrameChildOptionValuesArr.length > 0) {
            //프레임 값
            const selectedFrameChildOptionValuesStr = selectedFrameChildOptionValuesArr.join('^');
            temp.push(`&prop.${optionsConstant.childOptions['shot'].id}=${selectedFrameChildOptionValuesStr}`);
          }
        }

        //가로앵글
        if (storedStandard.horizontalAngle) {
          const selectedHorizontalAngleChildOptionValuesArr = Object.values(storedStandard.horizontalAngle)
            .filter((item) => item.isSelected)
            .map((item) => item.value);

          //선택한 가로앵글 값이 1개 이상일 경우에만 진행
          if (selectedHorizontalAngleChildOptionValuesArr.length > 0) {
            //가로앵글 값
            const selectedHorizontalAngleChildOptionValuesStr = selectedHorizontalAngleChildOptionValuesArr.join('^');
            temp.push(
              `&prop.${optionsConstant.childOptions['horizontalAngle'].id}=${selectedHorizontalAngleChildOptionValuesStr}`
            );
          }
        }

        //세로앵글
        if (storedStandard.verticalAngle) {
          const selectedVerticalAngleChildOptionValuesArr = Object.values(storedStandard.verticalAngle)
            .filter((item) => item.isSelected)
            .map((item) => item.value);

          //선택한 세로앵글 값이 1개 이상일 경우에만 진행
          if (selectedVerticalAngleChildOptionValuesArr.length > 0) {
            //세로앵글 값
            const selectedVerticalAngleChildOptionValuesStr = selectedVerticalAngleChildOptionValuesArr.join('^');
            temp.push(
              `&prop.${optionsConstant.childOptions['verticalAngle'].id}=${selectedVerticalAngleChildOptionValuesStr}`
            );
          }
        }
      }

      //parentOption={스탠다드}
      temp.push(`&parentOptions=${optionsConstant.parentOptions['STANDARD'].id}`);

      // 배경컷
      if (storedStandard.backgroundCut) {
        const selectedOption = Object.values(storedStandard.backgroundCut).find((option) => option.isSelected);

        if (selectedOption?.value) {
          temp.push(`&prop.${optionsConstant.childOptions['backgroundCut'].id}=${selectedOption.value}`);
        }
      }

      // 연출
      if (storedStandard.directed) {
        const selectedOption = Object.values(storedStandard.directed).find((option) => option.isSelected);

        if (selectedOption?.value) {
          temp.push(`&prop.${optionsConstant.childOptions['directed'].id}=${selectedOption.value}`);
        }
      }

      //데포르메
      if (storedStandard.deforme) {
        const selectedOption = Object.values(storedStandard.deforme).find((option) => option.isSelected);

        if (selectedOption?.value) {
          temp.push(`&prop.${optionsConstant.childOptions['deforme'].id}=${selectedOption.value}`);
        }
      }

      //스킨십
      if (storedStandard.skinship) {
        const selectedOption = Object.values(storedStandard.skinship).find((option) => option.isSelected);

        if (selectedOption?.value) {
          temp.push(`&prop.${optionsConstant.childOptions['skinship'].id}=${selectedOption.value}`);
        }
      }

      //연령대
      if (storedStandard.ageRange) {
        const selectedAgeRangeChildOptionValuesArr = Object.values(storedStandard.ageRange)
          .filter((item) => item.isSelected)
          .map((item) => item.value);

        //선택한 연령대 값이 1개 이상일 경우에만 진행
        if (selectedAgeRangeChildOptionValuesArr.length > 0) {
          //연령대 값
          const selectedFrameChildOptionValuesStr = selectedAgeRangeChildOptionValuesArr.join('^');
          temp.push(`&prop.${optionsConstant.childOptions['ageRange'].id}=${selectedFrameChildOptionValuesStr}`);
        }
      }

      //중심동작부위
      if (storedStandard.mainMotionPart) {
        const selectedMainMotionPartChildOptionValuesArr = Object.values(storedStandard.mainMotionPart)
          .filter((item) => item.isSelected)
          .map((item) => item.value);

        //선택한 중심동작부위 값이 1개 이상일 경우에만 진행
        if (selectedMainMotionPartChildOptionValuesArr.length > 0) {
          //중심동작부위 값
          const selectedFrameChildOptionValuesStr = selectedMainMotionPartChildOptionValuesArr.join('^');
          temp.push(`&prop.${optionsConstant.childOptions['mainMotionPart'].id}=${selectedFrameChildOptionValuesStr}`);
        }
      }

      //표정
      if (storedStandard.facialExpression) {
        const selectedFacialExpressionChildOptionValuesArr = Object.values(storedStandard.facialExpression)
          .filter((item) => item.isSelected)
          .map((item) => item.value);

        //선택한 표정 값이 1개 이상일 경우에만 진행
        if (selectedFacialExpressionChildOptionValuesArr.length > 0) {
          //표정 값
          const selectedFrameChildOptionValuesStr = selectedFacialExpressionChildOptionValuesArr.join('^');
          temp.push(
            `&prop.${optionsConstant.childOptions['facialExpression'].id}=${selectedFrameChildOptionValuesStr}`
          );
        }
      }

      //동작구분
      if (storedStandard.postureCategory) {
        const selectedPostureCategoryChildOptionValuesArr = Object.values(storedStandard.postureCategory)
          .filter((item) => item.isSelected)
          .map((item) => item.value);

        //선택한 동작구분 값이 1개 이상일 경우에만 진행
        if (selectedPostureCategoryChildOptionValuesArr.length > 0) {
          //동작구분 값
          const selectedHorizontalAngledChildOptionValuesStr = selectedPostureCategoryChildOptionValuesArr.join('^');
          temp.push(
            `&prop.${optionsConstant.childOptions['postureCategory'].id}=${selectedHorizontalAngledChildOptionValuesStr}`
          );
        }
      }

      //태그
      if (selectedMetaTags && selectedMetaTags.length > 0) {
        const selectedMetaTagNameStr = selectedMetaTags.map((item) => `metaTag=${item}`).join('&');
        temp.push(`&${selectedMetaTagNameStr}`);
      }
      if (aiRecommendedMetaTags && aiRecommendedMetaTags.length > 0) {
        const aiRecommendedMetaTagNameStr = aiRecommendedMetaTags.map((item) => `metaTag=${item}`).join('&');
        temp.push(`&${aiRecommendedMetaTagNameStr}`);
      }
    }
    //19금
    else if (locationPathname === PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE) {
      temp.push(`&take=${otherOutputTake}`);

      //장르
      if (selectedGenreList && selectedGenreList.length > 0) {
        const selectedGenreStr = selectedGenreList.map((item) => `genre=${encodeURIComponent(item)}`).join('&');
        temp.push(`&${selectedGenreStr}`);
      }
      //작품명
      if (selectedComicNameList && selectedComicNameList.length > 0) {
        const selectedComicNameStr = selectedComicNameList.map((item) => `comic=${encodeURIComponent(item)}`).join('&');
        temp.push(`&${selectedComicNameStr}`);
      }

      // 내가 사용한 이미지 포함 여부
      temp.push(`&isIncludeUsed=${storedAdultsOnly.isIncludeUsed}`);

      // 남이 사용한 이미지 포함 여부
      temp.push(`&isIncludeOtherUsersUsed=${storedAdultsOnly.isIncludeOtherUsersUsed}`);

      // 정렬 기준
      temp.push(`&sortType=${storedAdultsOnly.sortType}`);

      //parentOption이 GENERAL인 옵션에 대한 query param 생성
      //parentOption이 GENERAL이 아닌 옵션에 대한 query param보다 앞 쪽에 위치하여야 한다 (14차 스프린트 사항)
      //인원수, 샷구분(=19금 프레임), 가로앵글, 세로앵글
      //general이 아닌 adultsOnly store에서 가져온 값
      if (
        storedAdultsOnly.characterNumber ||
        storedAdultsOnly.shot ||
        storedAdultsOnly.horizontalAngle ||
        storedAdultsOnly.verticalAngle
      ) {
        temp.push(`&parentOptions=${optionsConstant.parentOptions['GENERAL'].id}`);

        //인원수
        if (storedAdultsOnly.characterNumber) {
          temp.push(
            `&prop.${
              optionsConstant.childOptions['maleNumber'].id
            }=${encodeURIComponent(storedAdultsOnly.characterNumber.maleNumber ?? 0)}&prop.${optionsConstant.childOptions['femaleNumber'].id}=${encodeURIComponent(storedAdultsOnly.characterNumber.femaleNumber ?? 0)}`
          );
        }

        //프레임
        if (storedAdultsOnly.shot) {
          const selectedFrameChildOptionValuesArr = Object.values(storedAdultsOnly.shot)
            .filter((item) => item.isSelected)
            .map((item) => item.value);

          //선택한 프레임 값이 1개 이상일 경우에만 진행
          if (selectedFrameChildOptionValuesArr.length > 0) {
            //프레임 값
            const selectedFrameChildOptionValuesStr = selectedFrameChildOptionValuesArr.join('^');
            temp.push(`&prop.${optionsConstant.childOptions['shot'].id}=${selectedFrameChildOptionValuesStr}`);
          }
        }

        //가로앵글
        if (storedAdultsOnly.horizontalAngle) {
          const selectedHorizontalAngleChildOptionValuesArr = Object.values(storedAdultsOnly.horizontalAngle)
            .filter((item) => item.isSelected)
            .map((item) => item.value);

          //선택한 가로앵글 값이 1개 이상일 경우에만 진행
          if (selectedHorizontalAngleChildOptionValuesArr.length > 0) {
            //가로앵글 값
            const selectedhorizontalAngleChildOptionValuesStr = selectedHorizontalAngleChildOptionValuesArr.join('^');
            temp.push(
              `&prop.${optionsConstant.childOptions['horizontalAngle'].id}=${selectedhorizontalAngleChildOptionValuesStr}`
            );
          }
        }

        //세로앵글
        if (storedAdultsOnly.verticalAngle) {
          const selectedVerticalAngleChildOptionValuesArr = Object.values(storedAdultsOnly.verticalAngle)
            .filter((item) => item.isSelected)
            .map((item) => item.value);

          //선택한 세로앵글 값이 1개 이상일 경우에만 진행
          if (selectedVerticalAngleChildOptionValuesArr.length > 0) {
            //세로앵글 값
            const selectedverticalAngleChildOptionValuesStr = selectedVerticalAngleChildOptionValuesArr.join('^');
            temp.push(
              `&prop.${optionsConstant.childOptions['verticalAngle'].id}=${selectedverticalAngleChildOptionValuesStr}`
            );
          }
        }
      }

      //parentOption={19금}
      temp.push(`&parentOptions=${optionsConstant.parentOptions['ADULTS_ONLY'].id}`);

      // 체위
      temp.push(`&prop.${optionsConstant.childOptions['sexualPosition'].id}=${storedAdultsOnly.sexualPosition}`);

      //여체
      if (storedAdultsOnly.femaleBody) {
        const selectedFemaleBodyChildOptionValuesArr = Object.values(storedAdultsOnly.femaleBody)
          .filter((item) => item.isSelected)
          .map((item) => item.value);

        //선택한 여체이 1개 이상일 경우에만 진행
        if (selectedFemaleBodyChildOptionValuesArr.length > 0) {
          //여체
          const selectedFemaleBodyChildOptionValuesStr = selectedFemaleBodyChildOptionValuesArr.join('^');
          temp.push(`&prop.${optionsConstant.childOptions['femaleBody'].id}=${selectedFemaleBodyChildOptionValuesStr}`);
        }
      }

      //19금 신체 부위
      if (storedAdultsOnly.sexualBodyPart) {
        const selectedSexualBodyPartChildOptionValuesArr = Object.values(storedAdultsOnly.sexualBodyPart)
          .filter((item) => item.isSelected)
          .map((item) => item.value);

        //선택한 19금 신체 부위 값이 1개 이상일 경우에만 진행
        if (selectedSexualBodyPartChildOptionValuesArr.length > 0) {
          //19금 신체 부위 값
          const selectedSexualBodyPartChildOptionValuesStr = selectedSexualBodyPartChildOptionValuesArr.join('^');
          temp.push(
            `&prop.${optionsConstant.childOptions['sexualBodyPart'].id}=${selectedSexualBodyPartChildOptionValuesStr}`
          );
        }
      }

      //태그
      if (selectedMetaTags && selectedMetaTags.length > 0) {
        const selectedMetaTagNameStr = selectedMetaTags.map((item) => `metaTag=${item}`).join('&');
        temp.push(`&${selectedMetaTagNameStr}`);
      }
    }
    //액션 장르
    else if (locationPathname === PathnameEnum.ACTION_GENRE_OUTPUT_PAGE) {
      temp.push(`&take=${otherOutputTake}`);

      //인원수
      //'&parentOptions=1'은 parentOption이 GENERAL이 아닌 옵션에 대한 query param보다 앞 쪽에 위치하여야 한다 (14차 스프린트 사항)
      temp.push(
        `&parentOptions=${optionsConstant.parentOptions['GENERAL'].id}&prop.${optionsConstant.childOptions['wholeGenderNumber'].id}=${encodeURIComponent(storedActionGenre.wholeGenderNumber)}`
      );

      //액션 장르 차일드 옵션 값
      temp.push(
        `&parentOptions=${optionsConstant.parentOptions['ACTION'].id}&prop.${optionsConstant.childOptions['action'].id}=${storedActionGenre.childOptionValue}`
      );
    }
    //신체부위
    else if (locationPathname === PathnameEnum.BODY_PART_OUTPUT_PAGE) {
      const selectedBodyPartChildOptionValuesArr = Object.keys(storedBodyPartChildOptionValue).filter(
        (key) => storedBodyPartChildOptionValue[key]
      );

      //선택한 신체부위 차일드 옵션 값이 1개 이상일 경우에만 진행
      if (selectedBodyPartChildOptionValuesArr.length > 0) {
        temp.push(`&take=${otherOutputTake}`);

        //parentOption={스탠다드}
        temp.push(`&parentOptions=${optionsConstant.parentOptions['STANDARD'].id}`);

        //신체부위 차일드 옵션 값
        const selectedBodyPartChildOptionValuesStr = selectedBodyPartChildOptionValuesArr.join('^');
        temp.push(`&prop.${optionsConstant.childOptions['bodyPart'].id}=${selectedBodyPartChildOptionValuesStr}`);
      }
    }
    //표정
    else if (locationPathname === PathnameEnum.FACIAL_EXPRESSION_OUTPUT_PAGE) {
      const selectedFacialExpressionChildOptionValuesArr = Object.keys(storedFacialExpressionChildOptionValue).filter(
        (key) => storedFacialExpressionChildOptionValue[key]
      );

      //선택한 표정 차일드 옵션 값이 1개 이상일 경우에만 진행
      if (selectedFacialExpressionChildOptionValuesArr.length > 0) {
        temp.push(`&take=${otherOutputTake}`);

        //parentOption={스탠다드}
        temp.push(`&parentOptions=${optionsConstant.parentOptions['STANDARD'].id}`);

        //표정 차일드 옵션 값
        const selectedFacialExpressionChildOptionValuesStr = selectedFacialExpressionChildOptionValuesArr.join('^');
        temp.push(
          `&prop.${optionsConstant.childOptions['facialExpression'].id}=${selectedFacialExpressionChildOptionValuesStr}`
        );
      }
    }
    //기타
    else if (locationPathname === PathnameEnum.ETC_OUTPUT_PAGE) {
      const selectedEtcChildOptionValuesArr = Object.keys(storedEtcChildOptionValue).filter(
        (key) => storedEtcChildOptionValue[key]
      );

      //선택한 기타 차일드 옵션 값이 1개 이상일 경우에만 진행
      if (selectedEtcChildOptionValuesArr.length > 0) {
        temp.push(`&take=${otherOutputTake}`);

        //parentOption={기타}
        temp.push(`&parentOptions=${optionsConstant.parentOptions['ETC'].id}`);

        //기타 차일드 옵션 값
        const selectedEtcChildOptionValuesStr = selectedEtcChildOptionValuesArr.join('^');
        temp.push(`&prop.${optionsConstant.childOptions['etc'].id}=${selectedEtcChildOptionValuesStr}`);
      }
    }

    const qs = temp.join('');

    return qs;
  };

  const { data, hasNextPage, fetchNextPage, isLoading, isFetching } = useFetchImageList(
    createQueryStringForImageList(),
    {
      onSuccess: (data, variables) => {
        // pageParam이 1인 경우에만 스크롤업 실행
        if (variables.pageParam === 1) {
          if (scrollToTop) {
            scrollToTop();
          } else {
            // 기본 동작 (다른 페이지들용)
            window.scrollTo({
              top: 0
            });
          }
        }
      }
    }
  );

  const { mutateAsync: putImageUseMutateAsync } = usePutImageUse((err) => {
    // PUT 요청 실패 시 캐시 데이터 복구
    if (data) {
      const currentQueryKey = ['get', `/images${createQueryStringForImageList()}`];

      // 실패한 요청의 imageId 추출
      const failedImageId = Number(err.config?.url?.split('/').pop());

      // 이전 상태로 데이터 복구
      const restoredPages = data.pages.map((page) => ({
        ...page,
        data: {
          ...page.data,
          data: page.data.data.map((item: IImageListItem) => {
            if (item.id === failedImageId) {
              return {
                ...item,
                isUsed: !item.isUsed, // 토글된 상태를 다시 원래대로
                usedCount: item.isUsed ? item.usedCount - 1 : item.usedCount + 1 // 변경된 usedCount를 다시 원래대로
              };
            }
            return item;
          })
        }
      }));

      // 캐시 업데이트
      queryClient.setQueryData(currentQueryKey, (oldData: any) => ({
        pageParams: oldData.pageParams,
        pages: restoredPages
      }));

      // 리렌더링 트리거
      queryClient.invalidateQueries({
        queryKey: currentQueryKey,
        exact: true,
        refetchType: 'none'
      });
    }
  });

  const flattenImageList = useMemo(() => {
    const dataArrLength = data?.pages.length;
    const flattenedArr: IImageListItem[] = [];

    if (dataArrLength) {
      let i = 0;
      while (i < dataArrLength) {
        if (data.pages[i].data.data.length > 0) {
          data.pages[i].data.data.map((item: IImageListItem) => {
            flattenedArr.push(item);
          });
        }
        i++;
      }
      return flattenedArr;
    } else {
      return [];
    }
  }, [data]);

  const handleImageClick = (imageId: number, sequenceId?: number, s3Url?: string) => {
    const isStandardOutputPage = locationPathname === PathnameEnum.STANDARD_OUTPUT_PAGE; // 스탠다드 Output 페이지인 경우 true
    const isAdultsOnlyOutputPage = locationPathname === PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE; // 19금 Output 페이지인 경우 true
    const isCollectionImageListPage = /^\/collections\/\d+$/.test(locationPathname); //pathname이 /collections/숫자 패턴과 일치하는지 검사
    const isUsedImageListPage = locationPathname === PathnameEnum.USED_IMAGE_LIST_PAGE;
    const showImageDetailModal =
      isStandardOutputPage || isAdultsOnlyOutputPage || isCollectionImageListPage || isUsedImageListPage; //단일컷 또는 시퀀스컷 이미지 상세페이지 보여줘야할 시 true

    const currentSearch = location.search;
    const queryParams = new URLSearchParams(currentSearch);

    queryParams.set(QueryParamKeyEnum.IS_IMAGE_MODAL_VISIBLE, 'true');

    if (showImageDetailModal) {
      //단일컷 또는 시퀀스컷 이미지 상세페이지
      queryParams.set(QueryParamKeyEnum.IMAGE_ID, imageId.toString());
      if (sequenceId) {
        queryParams.set(QueryParamKeyEnum.SEQUENCE_ID, sequenceId.toString());
      }
    } else {
      //단순 이미지 모달
      if (s3Url) {
        queryParams.set(QueryParamKeyEnum.S3_URL, s3Url);
      }
    }

    navigate(`${locationPathname}?${queryParams.toString()}`);
  };

  const handleImageModalClose = () => {
    const currentSearch = location.search;
    const queryParams = new URLSearchParams(currentSearch);

    queryParams.delete(QueryParamKeyEnum.IS_IMAGE_MODAL_VISIBLE);
    queryParams.delete(QueryParamKeyEnum.IMAGE_ID);
    queryParams.delete(QueryParamKeyEnum.SEQUENCE_ID);
    queryParams.delete(QueryParamKeyEnum.S3_URL);

    navigate(`${locationPathname}?${queryParams.toString()}`);
  };

  // 스탠다드 및 19금 Output 페이지 '내가 사용한 이미지' 버튼 클릭시 실행
  const handleIncludeUsedToggle = () => {
    if (locationPathname === PathnameEnum.STANDARD_OUTPUT_PAGE) {
      onSetStandard({ isIncludeUsed: !storedStandard.isIncludeUsed });
    } else if (locationPathname === PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE) {
      onSetAdultsOnly({ isIncludeUsed: !storedAdultsOnly.isIncludeUsed });
    }
  };

  // 스탠다드 및 19금 Output 페이지 '남이 사용한 이미지' 버튼 클릭시 실행
  const handleIncludeOtherUsersUsedToggle = () => {
    if (locationPathname === PathnameEnum.STANDARD_OUTPUT_PAGE) {
      onSetStandard({ isIncludeOtherUsersUsed: !storedStandard.isIncludeOtherUsersUsed });
    } else if (locationPathname === PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE) {
      onSetAdultsOnly({ isIncludeOtherUsersUsed: !storedAdultsOnly.isIncludeOtherUsersUsed });
    }
  };

  // 스탠다드 및 19금 Output 페이지 이미지 '사용' 버튼 클릭시 실행
  const handleImageUseButtonClick = (imageId: number, isUsed: boolean, usedCount: number) => {
    if (data) {
      // 1. useFetchImageList와 동일한 쿼리 키 사용
      const currentQueryKey = ['get', `/images${createQueryStringForImageList()}`];

      // 2. 새로운 페이지 데이터 생성
      const newPages = data.pages.map((page) => ({
        ...page,
        data: {
          ...page.data,
          data: page.data.data.map((item: IImageListItem) => {
            if (item.id === imageId) {
              return {
                ...item,
                isUsed: !isUsed,
                usedCount: !isUsed ? usedCount + 1 : usedCount - 1
              };
            }
            return item;
          })
        }
      }));

      // 3. 캐시 업데이트
      queryClient.setQueryData(currentQueryKey, (oldData: any) => ({
        pageParams: oldData.pageParams,
        pages: newPages
      }));

      // 4. 쿼리 무효화하여 리렌더링 트리거
      queryClient.invalidateQueries({
        queryKey: currentQueryKey,
        exact: true,
        refetchType: 'none' // 실제 API 호출은 하지 않고 캐시만 업데이트
      });

      // 5. PUT /images/used-image/:id 요청
      putImageUseMutateAsync(imageId).then(() => {
        // 요청 성공시 토스트 메세지 보여주기
        showToast(isUsed ? ToastMessageEnum.CANCEL_IMAGE_USE : ToastMessageEnum.ADD_IMAGE_USE);
      });
    }
  };

  // 이미지 리스트 다음 페이지 호출
  useEffect(() => {
    if (outputBottomRefCurrent) {
      const observerCallback: IntersectionObserverCallback = (entries) => {
        entries.forEach((entry) => {
          //target이 화면에 관찰되고, 다음페이지가 있고, API 요청 처리중이 아니라면 다음페이지를 호출
          if (entry.isIntersecting && hasNextPage && !isLoading && !isFetching) {
            fetchNextPage();
          }
        });
      };

      //ointersection observer 인스턴스 생성
      const observer = new IntersectionObserver(observerCallback, {
        threshold: 1.0
      });

      // 타겟 관찰 시작
      observer.observe(outputBottomRefCurrent);

      // 관찰 멈춤
      return () => observer.unobserve(outputBottomRefCurrent);
    }
  }, [outputBottomRefCurrent, hasNextPage, isLoading, isFetching, fetchNextPage]);

  // Output 페이지 장르 및 작품명 설정 -> 타페이지 이동 -> 뒤로가기시 장르 및 작품명 남아있지 않도록 처리
  useEffect(() => {
    // 스탠다드 검색 Output 페이지 또는 19금 Output 페이지에서 쿼리 파라미터가 있는 경우 히스토리를 기본 URL로 업데이트
    if (
      locationPathname === PathnameEnum.STANDARD_OUTPUT_PAGE ||
      locationPathname === PathnameEnum.ADULTS_ONLY_OUTPUT_PAGE
    ) {
      const currentPathname = window.location.pathname;
      const currentSearch = window.location.search;

      // 현재 URL에 쿼리 파라미터가 있는 경우 히스토리를 기본 URL로 업데이트
      if (currentSearch && currentSearch.length > 0) {
        // navigate를 사용하여 기본 URL로 이동 (replace: true로 히스토리 교체)
        navigate(currentPathname, { replace: true });
      }
    }
  }, [locationPathname, navigate]);

  return {
    //standard & adults only
    handleIncludeUsedToggle,
    handleIncludeOtherUsersUsedToggle,
    handleImageUseButtonClick,

    //standard
    minColumnNum,
    maxColumnNum,
    columnNum,
    setColumnNum,
    storedStandard,
    onSetStandard,

    //special - adults only
    storedAdultsOnly,
    onSetAdultsOnly,

    //special - action genre
    storedActionGenre,
    onSetActionGenre,

    //special - body part
    storedBodyPartChildOptionValue,
    onSetBodyPart,

    //special - facial expression
    storedFacialExpressionChildOptionValue,
    onSetFacialExpression,

    //special - etc
    storedEtcChildOptionValue,
    onSetEtc,

    locationPathname,
    data,
    isLoading,
    flattenImageList,
    outputBottomRef,
    createQueryStringForImageList,

    handleImageClick,
    handleImageModalClose
  };
};
